import React, { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import {
  Box, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography,
  CircularProgress, FormControl, InputLabel, Select, MenuItem, Tabs, Tab, Badge,
  IconButton, Chip, TableSortLabel, Collapse, Menu, Autocomplete,
} from '@mui/material';
import InputIcon from '@mui/icons-material/Input';
import OutputIcon from '@mui/icons-material/Output';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VerifiedIcon from '@mui/icons-material/Verified';
import { useNavigate } from 'react-router-dom';
import {
  productionOrderService, finishedGoodsReceiptService, finishedGoodsShipmentService,
  finishedGoodsBalancesService, stockTransactionService, stockItemService, productService,
  staffService,
} from '../services/api';
import api from '../services/api';
import ManualWarehouseTransferDialog from '../components/ManualWarehouseTransferDialog';
import { navigateWithReturn, stockReturnLabel, stockReturnTo } from '../utils/pageBack';
import { orderRefFromRow } from '../utils/orderLabel';

const fmtDt = (v) => v ? new Date(v).toLocaleString('uk-UA') : '—';

const FG_MANUAL_RECEIPT_PERM = 'menu.stock.finished.manual_receipt';

function readPermsFromStorage() {
  try {
    const u = JSON.parse(localStorage.getItem('currentUser') || 'null');
    return u?.effective_permissions || {};
  } catch {
    return {};
  }
}

/** Право на ручний прихід. Явний false ховає кнопку; інакше показуємо (бекенд все одно перевірить). */
function canManualFgReceipt(perms) {
  const p = perms || {};
  if (p[FG_MANUAL_RECEIPT_PERM] === false) return false;
  return true;
}

const FG_MANUAL_REASONS = [
  { id: 'inventory', name: 'Інвентаризація' },
  { id: 'co_product', name: 'Супутній виріб з прес-форми' },
  { id: 'return', name: 'Повернення від клієнта' },
  { id: 'other', name: 'Інше' },
];

const emptyArrivalForm = () => ({
  product_id: '',
  quantity: '',
  reason: '',
  note: '',
});

/** Select у Dialog: обмежена висота + скрол (інакше список ховається за низ екрана). */
const SELECT_MENU_PROPS = {
  PaperProps: {
    sx: { maxHeight: 'min(40vh, 320px)' },
  },
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
};

function FinishedGoodsStockPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [receipts, setReceipts] = useState([]);

  // Extra header state
  const [transferHubOpen, setTransferHubOpen] = useState(false);
  const [shortageOpen, setShortageOpen] = useState(false);
  const [shortages, setShortages] = useState([]);
  const [shortageLoading, setShortageLoading] = useState(false);
  const [refsAnchorEl, setRefsAnchorEl] = useState(null);
  const refsMenuOpen = Boolean(refsAnchorEl);
  const [shipments, setShipments] = useState([]);
  const [balances, setBalances] = useState({ by_product: [], by_order_product: [] });
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [opTypeFilter, setOpTypeFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [qtyFilter, setQtyFilter] = useState('all'); // all | gt0 | eq0 | lt0
  const [balSortKey, setBalSortKey] = useState('name'); // name | article | quantity
  const [balSortDir, setBalSortDir] = useState('asc');

  // Arrival (manual free stock)
  const [arrivalOpen, setArrivalOpen] = useState(false);
  const [arrivalForm, setArrivalForm] = useState(emptyArrivalForm);
  const [arrivalProducts, setArrivalProducts] = useState([]);
  const [arrivalSaving, setArrivalSaving] = useState(false);
  const [arrivalError, setArrivalError] = useState('');
  const [arrivalConfirmOpen, setArrivalConfirmOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [confirmQuantity, setConfirmQuantity] = useState('');

  // Shipment (multi-line)
  const emptyShipmentLine = () => ({
    key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    order_id: '',
    product_id: '',
    quantity: '',
    packaging: [],
  });
  const [shipmentOpen, setShipmentOpen] = useState(false);
  const [shipmentLines, setShipmentLines] = useState([emptyShipmentLine()]);
  const [shipmentSaving, setShipmentSaving] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      const [ordersRes, receiptsRes, shipmentsRes, balancesRes] = await Promise.all([
        // Для ручного «Прихід» — завершені; для «Вибуття» список будуємо з залишків (див. shipmentStockOptions)
        productionOrderService.getAll({ status: 'completed', page_size: 500 }),
        finishedGoodsReceiptService.getAll(),
        finishedGoodsShipmentService.getAll(),
        finishedGoodsBalancesService.getBalances(),
      ]);
      setOrders(ordersRes.data.results || ordersRes.data || []);
      const allReceipts = receiptsRes.data.results || receiptsRes.data || [];
      setReceipts(allReceipts.filter((r) => !r.warehouse || r.warehouse === 'Склад готової продукції'));
      setShipments(shipmentsRes.data.results || shipmentsRes.data || []);
      setBalances(balancesRes.data || { by_product: [], by_order_product: [] });
    } catch (error) {
      console.error('Помилка завантаження:', error);
      alert('Помилка завантаження даних');
    } finally {
      setLoading(false);
    }
  };

  const [canManualReceipt, setCanManualReceipt] = useState(() => canManualFgReceipt(readPermsFromStorage()));

  useEffect(() => { loadData(); }, []);

  // Оновити права з БД (після міграції стара сесія не має menu.stock.finished.manual_receipt)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const raw = JSON.parse(localStorage.getItem('currentUser') || 'null');
        if (!raw?.id) {
          if (!cancelled) setCanManualReceipt(true);
          return;
        }
        const staffRes = await staffService.getById(raw.id);
        const staff = staffRes.data || {};
        let rolePerms = {};
        const roleId = staff.role ?? raw.role;
        if (roleId) {
          try {
            const roleRes = await api.get(`/roles/${roleId}/`);
            rolePerms = roleRes.data?.permissions || {};
          } catch { /* ignore */ }
        }
        const staffPerms = staff.permissions || {};
        const effective = { ...rolePerms, ...staffPerms };
        const next = { ...raw, ...staff, effective_permissions: effective };
        localStorage.setItem('currentUser', JSON.stringify(next));
        if (!cancelled) setCanManualReceipt(canManualFgReceipt(effective));
      } catch {
        if (!cancelled) setCanManualReceipt(canManualFgReceipt(readPermsFromStorage()));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const FREE_ORDER_KEY = 'free';

  const isFreeOrderId = (orderId) => (
    orderId == null || orderId === '' || String(orderId) === FREE_ORDER_KEY
  );

  const lineStockKey = (orderId, productId) => (
    `${isFreeOrderId(orderId) ? FREE_ORDER_KEY : orderId}:${productId}`
  );

  /** Позиції з додатним залишком на СГП — і під замовленням, і вільний залишок. */
  const shipmentStockOptions = useMemo(() => {
    return (balances.by_order_product || [])
      .filter((r) => Number(r.quantity) > 0)
      .slice()
      .sort((a, b) => {
        const aFree = a.order_id == null;
        const bFree = b.order_id == null;
        if (aFree !== bFree) return aFree ? -1 : 1; // вільний залишок зверху
        const an = String(a.order_label || a.customer_order_number || a.order_id || '');
        const bn = String(b.order_label || b.customer_order_number || b.order_id || '');
        const cmp = an.localeCompare(bn, 'uk');
        if (cmp !== 0) return cmp;
        return String(a.product_name || '').localeCompare(String(b.product_name || ''), 'uk');
      });
  }, [balances]);

  const findStockOption = (orderId, productId) => {
    const pid = Number(productId);
    if (!pid) return null;
    const free = isFreeOrderId(orderId);
    return shipmentStockOptions.find((r) => {
      if (Number(r.product_id) !== pid) return false;
      if (free) return r.order_id == null;
      return Number(r.order_id) === Number(orderId);
    }) || null;
  };

  /** Скільки вже зайнято іншими рядками цієї ж форми (order+product). */
  const reservedQtyFor = (orderId, productId, exceptKey) => {
    const k = lineStockKey(orderId, productId);
    return shipmentLines.reduce((sum, line) => {
      if (line.key === exceptKey) return sum;
      if (lineStockKey(line.order_id, line.product_id) !== k) return sum;
      const q = parseInt(line.quantity, 10);
      return sum + (Number.isFinite(q) && q > 0 ? q : 0);
    }, 0);
  };

  // Надходження: лише вже передані зі СВ (не «Склад виробництва») і ще не підтверджені
  const pendingReceipts = receipts.filter(
    (r) => !r.warehouse_confirmed
      && (r.warehouse || '') !== 'Склад виробництва',
  );
  const pendingCount = pendingReceipts.length;
  const normalizedSearch = searchQuery.trim().toLowerCase();
  const searchTokens = normalizedSearch ? normalizedSearch.split(/\s+/u).filter(Boolean) : [];

  const matchesSearch = (article, name) => {
    if (!searchTokens.length) return true;
    const a = (article || '').toLowerCase();
    const n = (name || '').toLowerCase();
    return searchTokens.every((t) => a.includes(t) || n.includes(t));
  };

  const stockTotalByProduct = (productId) => {
    const row = (balances.by_product || []).find((r) => r.product_id === productId);
    return row ? row.total_quantity : 0;
  };

  // ---------- Arrival (ручний вільний залишок) ----------
  const handleArrivalOpen = async () => {
    if (!canManualReceipt) {
      alert('Немає права на ручний прихід ГП (menu.stock.finished.manual_receipt)');
      return;
    }
    setArrivalError('');
    setArrivalForm(emptyArrivalForm());
    setArrivalOpen(true);
    try {
      const res = await productService.getAll();
      const list = res.data?.results ?? res.data ?? [];
      setArrivalProducts(Array.isArray(list) ? list : []);
    } catch {
      setArrivalProducts([]);
    }
  };

  const handleArrivalSubmit = async () => {
    setArrivalError('');
    if (!arrivalForm.product_id) {
      setArrivalError('Оберіть виріб');
      return;
    }
    const qty = parseInt(arrivalForm.quantity, 10);
    if (!Number.isFinite(qty) || qty <= 0) {
      setArrivalError('Вкажіть коректну кількість');
      return;
    }
    if (!arrivalForm.reason) {
      setArrivalError('Оберіть причину');
      return;
    }
    setArrivalSaving(true);
    try {
      await finishedGoodsReceiptService.manualIn({
        product: Number(arrivalForm.product_id),
        quantity: qty,
        reason: arrivalForm.reason,
        note: arrivalForm.note || '',
      });
      setArrivalOpen(false);
      await loadData();
    } catch (e) {
      setArrivalError(
        e.response?.data?.error
        || e.response?.data?.detail
        || e.message
        || 'Помилка збереження',
      );
    } finally {
      setArrivalSaving(false);
    }
  };

  const handleOpenArrivalConfirm = (receipt) => {
    setSelectedReceipt(receipt);
    setConfirmQuantity(String(receipt.quantity ?? ''));
    setArrivalConfirmOpen(true);
  };
  const handleArrivalConfirmSubmit = async () => {
    if (!selectedReceipt) return;
    const qty = parseInt(confirmQuantity, 10);
    if (!Number.isFinite(qty) || qty <= 0) { alert('Вкажіть коректну кількість'); return; }
    try {
      await finishedGoodsReceiptService.confirm(selectedReceipt.id, { quantity: qty });
      setArrivalConfirmOpen(false);
      setSelectedReceipt(null);
      setConfirmQuantity('');
      loadData();
    } catch (e) { alert(e.response?.data?.detail || e.message || 'Помилка підтвердження'); }
  };

  // ---------- Shipment ----------
  const handleShipmentOpen = () => {
    setShipmentLines([emptyShipmentLine()]);
    setShipmentOpen(true);
  };

  const updateShipmentLine = (key, patch) => {
    setShipmentLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  };

  const handleShipmentLineSelect = async (key, orderId, productId) => {
    updateShipmentLine(key, {
      order_id: productId ? (isFreeOrderId(orderId) ? FREE_ORDER_KEY : String(orderId)) : '',
      product_id: productId || '',
      quantity: '',
      packaging: [],
    });
    if (!productId) return;
    try {
      const prodRes = await productService.getById(productId);
      updateShipmentLine(key, { packaging: prodRes.data.packaging_components || [] });
    } catch (_) {
      updateShipmentLine(key, { packaging: [] });
    }
  };

  const handleAddShipmentLine = () => {
    setShipmentLines((prev) => [...prev, emptyShipmentLine()]);
  };

  const handleRemoveShipmentLine = (key) => {
    setShipmentLines((prev) => (prev.length <= 1 ? prev : prev.filter((l) => l.key !== key)));
  };

  const handleShipmentSubmit = async () => {
    // product_id обовʼязковий; order_id='free' — вільний залишок
    const filledOk = shipmentLines.filter((l) => l.product_id && l.order_id);
    if (!filledOk.length) {
      alert('Додайте хоча б одну позицію');
      return;
    }

    const payloads = [];
    const used = {}; // order:product → вже запланована qty у цій формі

    for (let i = 0; i < filledOk.length; i += 1) {
      const line = filledOk[i];
      const stock = findStockOption(line.order_id, line.product_id);
      if (!stock) {
        alert(`Рядок ${i + 1}: позиція більше недоступна на складі`);
        return;
      }
      const qty = parseInt(line.quantity, 10);
      if (!Number.isFinite(qty) || qty <= 0) {
        alert(`Рядок ${i + 1}: вкажіть кількість до відвантаження`);
        return;
      }
      const k = lineStockKey(line.order_id, line.product_id);
      const already = used[k] || 0;
      const available = (Number(stock.quantity) || 0) - already;
      if (qty > available) {
        const label = stock.is_free || stock.order_id == null
          ? 'Вільний залишок'
          : (stock.order_label || orderRefFromRow(stock));
        alert(
          `Рядок ${i + 1}: по «${label} / ${stock.product_name}» доступно ${available} шт`
          + (already ? ` (з урахуванням інших рядків форми)` : ''),
        );
        return;
      }
      used[k] = already + qty;
      payloads.push({
        order: isFreeOrderId(line.order_id) ? null : Number(line.order_id),
        product: Number(line.product_id),
        quantity: qty,
      });
    }

    setShipmentSaving(true);
    try {
      await finishedGoodsShipmentService.bulkCreate(payloads);
      setShipmentOpen(false);
      loadData();
    } catch (e) {
      const d = e.response?.data?.detail;
      alert(
        (typeof d === 'string' ? d : null)
        || e.message
        || 'Помилка збереження (жоден рядок не списано — транзакція відкотилась)',
      );
      loadData();
    } finally {
      setShipmentSaving(false);
    }
  };

  // ---------- Sorting helpers ----------
  const handleSort = (field) => {
    if (sortField === field) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const handleExportOperations = () => {
    const rows = operations.map((op) => ({
      'Дата': fmtDt(op.date),
      'Тип': op.op_type === 'in' ? 'Прихід' : 'Відвантаження',
      'Замовлення / причина': op.order_display || (op.product_name ? `${op.product_name} x${op.quantity}` : '—'),
      'Артикул': op.product_article || '—',
      'Виріб': op.product_name || '—',
      'Кількість': op.quantity,
      'Хто': op.created_by || '',
      'Коментар': op.note || '',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Операції');
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`;
    const timeStr = `${pad(now.getHours())}-${pad(now.getMinutes())}`;
    XLSX.writeFile(wb, `Склад_готової_продукції_операції_${dateStr}_${timeStr}.xlsx`);
  };

  // ---------- Derived data ----------
  const operations = useMemo(() => {
    let items = [
      // У журнал потрапляють лише підтверджені складом приходи; непідтверджені — у «Надходженнях»
      ...receipts.filter((r) => r.warehouse_confirmed).map((r) => ({ ...r, op_type: 'in', date: r.created_at })),
      ...shipments.map((s) => ({ ...s, op_type: 'out', date: s.created_at })),
    ];
    if (opTypeFilter !== 'all') items = items.filter((op) => op.op_type === opTypeFilter);
    if (dateFrom) items = items.filter((op) => op.date && new Date(op.date) >= new Date(dateFrom));
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      items = items.filter((op) => op.date && new Date(op.date) <= to);
    }
    items = items.filter((op) => matchesSearch(op.product_article, op.product_name));
    items.sort((a, b) => {
      let av, bv;
      if (sortField === 'date') { av = new Date(a.date || 0); bv = new Date(b.date || 0); }
      else if (sortField === 'quantity') { av = a.quantity; bv = b.quantity; }
      else { av = (a.product_name || '').toLowerCase(); bv = (b.product_name || '').toLowerCase(); }
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return items;
  }, [receipts, shipments, opTypeFilter, dateFrom, dateTo, searchQuery, sortField, sortDir]);

  const filteredByProduct = useMemo(() => {
    let items = (balances.by_product || []).filter((row) => matchesSearch(row.product_article, row.product_name));
    if (qtyFilter === 'gt0') items = items.filter((r) => Number(r.total_quantity || 0) > 0);
    else if (qtyFilter === 'eq0') items = items.filter((r) => Number(r.total_quantity || 0) === 0);
    else if (qtyFilter === 'lt0') items = items.filter((r) => Number(r.total_quantity || 0) < 0);
    const sign = balSortDir === 'desc' ? -1 : 1;
    return [...items].sort((a, b) => {
      if (balSortKey === 'article') return String(a.product_article || '').localeCompare(String(b.product_article || ''), 'uk', { numeric: true, sensitivity: 'base' }) * sign;
      if (balSortKey === 'quantity') return (Number(a.total_quantity || 0) - Number(b.total_quantity || 0)) * sign;
      return String(a.product_name || '').localeCompare(String(b.product_name || ''), 'uk', { sensitivity: 'base' }) * sign;
    });
  }, [balances, searchQuery, qtyFilter, balSortKey, balSortDir]);
  const filteredPendingReceipts = useMemo(
    () => pendingReceipts.filter((r) => matchesSearch(r.product_article, r.product_name)),
    [pendingReceipts, searchQuery],
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h4">Готова продукція</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant={shortageOpen ? 'contained' : 'outlined'} color="warning" startIcon={<WarningAmberIcon />}
            onClick={() => {
              if (!shortageOpen) {
                setShortageLoading(true);
                api.get('/material-shortages/').then(r => setShortages(r.data || [])).catch(() => setShortages([])).finally(() => setShortageLoading(false));
              }
              setShortageOpen(o => !o);
            }}>
            Нестача {shortages.length > 0 && !shortageOpen ? `(${shortages.length})` : ''}
          </Button>
          <Button variant="contained" color="primary" startIcon={<MenuBookIcon />} onClick={(e) => setRefsAnchorEl(e.currentTarget)}>
            Довідники
          </Button>
          <Menu anchorEl={refsAnchorEl} open={refsMenuOpen} onClose={() => setRefsAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }}>
            {[
              ['/products', 'Вироби'],
              ['/raw-materials', 'Сировина'],
              ['/dyes', 'Фарби'],
              ['/packaging-materials', 'Упаковка'],
              ['/filament', 'Філамент'],
              ['/consumable-materials', 'Розхідні матеріали'],
            ].map(([path, label]) => (
              <MenuItem
                key={path}
                onClick={() => {
                  setRefsAnchorEl(null);
                  navigateWithReturn(navigate, path, stockReturnTo('finished'), stockReturnLabel('finished'));
                }}
              >
                {label}
              </MenuItem>
            ))}
          </Menu>
          {canManualReceipt && (
            <Button variant="contained" color="success" startIcon={<InputIcon />} onClick={handleArrivalOpen}>
              Прихід
            </Button>
          )}
          <Button variant="contained" color="error" startIcon={<OutputIcon />} onClick={handleShipmentOpen}>
            Вибуття
          </Button>
          <Button variant="contained" color="warning" startIcon={<SwapHorizIcon />} onClick={() => setTransferHubOpen(true)}>
            Переміщення
          </Button>
          <Button
            variant="outlined"
            color="info"
            startIcon={<VerifiedIcon />}
            onClick={() => navigateWithReturn(navigate, '/qc-kanban', stockReturnTo('finished'), stockReturnLabel('finished'))}
          >
            Контроль якості
          </Button>
        </Box>
      </Box>
      {shortageOpen && (
        <Box sx={{ mb: 1.5 }}>
          <Chip
            label="Активний фільтр: Нестача"
            color="warning"
            onDelete={() => setShortageOpen(false)}
            sx={{ fontWeight: 600 }}
          />
        </Box>
      )}
      <Collapse in={shortageOpen}>
        <Box sx={{ mb: 3, border: '1px solid', borderColor: 'warning.main', borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ px: 2, py: 1, bgcolor: 'warning.main' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'warning.contrastText' }}>
              ⚠️ Нестача матеріалів для запланованих виробничих замовлень
            </Typography>
          </Box>
          {shortageLoading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : shortages.length === 0 ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography color="success.main" fontWeight={600}>✅ Нестач немає</Typography>
            </Box>
          ) : (
            <TableContainer><Table size="small">
              <TableHead><TableRow>
                <TableCell>Матеріал</TableCell><TableCell align="right">Потрібно</TableCell>
                <TableCell align="right">Є на складі</TableCell><TableCell align="right">Нестача</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {shortages.map((s, i) => (
                  <TableRow key={i}>
                    <TableCell>{s.material_name || s.name}</TableCell>
                    <TableCell align="right">{s.required_quantity ?? '—'}</TableCell>
                    <TableCell align="right">{s.available_quantity ?? '—'}</TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontWeight: 600 }}>{s.shortage ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          )}
        </Box>
      </Collapse>

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            label="Пошук по артикулу або назві"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ flex: '1 1 340px' }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Фільтр</InputLabel>
            <Select label="Фільтр" value={qtyFilter} onChange={(e) => setQtyFilter(e.target.value)}>
              <MenuItem value="all">Усі</MenuItem>
              <MenuItem value="gt0">Тільки &gt; 0</MenuItem>
              <MenuItem value="eq0">Тільки = 0</MenuItem>
              <MenuItem value="lt0">Тільки &lt; 0</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Сортування</InputLabel>
            <Select label="Сортування" value={balSortKey} onChange={(e) => setBalSortKey(e.target.value)}>
              <MenuItem value="name">За назвою</MenuItem>
              <MenuItem value="article">За артикулом</MenuItem>
              <MenuItem value="quantity">За кількістю</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Напрям</InputLabel>
            <Select label="Напрям" value={balSortDir} onChange={(e) => setBalSortDir(e.target.value)}>
              <MenuItem value="asc">↑ Зростання</MenuItem>
              <MenuItem value="desc">↓ Спадання</MenuItem>
            </Select>
          </FormControl>
          <Button size="small" variant="outlined" onClick={() => {
            setSearchQuery('');
            setQtyFilter('all');
            setBalSortKey('name');
            setBalSortDir('asc');
          }}>
            Скинути
          </Button>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)}>
          <Tab label="Залишки" />
          <Tab label="Операції" />
          <Tab label={<Badge color="error" badgeContent={pendingCount || 0}>Надходження</Badge>} />
        </Tabs>
        {tabValue === 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2, flexWrap: 'wrap', flex: 1 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Тип операції</InputLabel>
              <Select label="Тип операції" value={opTypeFilter} onChange={(e) => setOpTypeFilter(e.target.value)}>
                <MenuItem value="all">Усі</MenuItem>
                <MenuItem value="in">Прихід</MenuItem>
                <MenuItem value="out">Відвантаження</MenuItem>
              </Select>
            </FormControl>
            <TextField size="small" type="date" label="З"
              value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            <TextField size="small" type="date" label="По"
              value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
            {(dateFrom || dateTo || opTypeFilter !== 'all') && (
              <Button size="small" onClick={() => { setOpTypeFilter('all'); setDateFrom(''); setDateTo(''); }}
                sx={{ textTransform: 'none', color: '#999' }}>
                Скинути
              </Button>
            )}
            <Box sx={{ ml: 'auto' }}>
              <Button variant="outlined" size="small"
                onClick={handleExportOperations}
                disabled={operations.length === 0}
                sx={{ textTransform: 'none', fontWeight: 600 }}>
                ⬇ Експорт ({operations.length})
              </Button>
            </Box>
          </Box>
        )}
      </Box>

      {/* ЗАЛИШКИ */}
      {tabValue === 0 && (
        <TableContainer component={Paper}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Артикул</TableCell>
                <TableCell>Виріб</TableCell>
                <TableCell align="right">На складі (шт)</TableCell>
                <TableCell align="right">Заблоковано ВКЯ (шт)</TableCell>
                <TableCell align="right">Доступно (шт)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredByProduct.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center"><Typography color="textSecondary">Немає залишків</Typography></TableCell></TableRow>
              ) : filteredByProduct.map((row) => {
                const available = Number(row.total_quantity || 0);
                const onStock = row.stock_quantity != null ? Number(row.stock_quantity) : available;
                // Утримане ВКЯ в межах фізичного залишку (блокування може охоплювати й ще не прийняте)
                const held = Math.max(0, onStock - available);
                return (
                  <TableRow key={row.product_id}>
                    <TableCell>{row.product_article || '—'}</TableCell>
                    <TableCell>{row.product_name}</TableCell>
                    <TableCell align="right">{onStock}</TableCell>
                    <TableCell align="right" sx={held > 0 ? { color: 'error.main' } : undefined}>
                      {held > 0 ? held : '—'}
                    </TableCell>
                    <TableCell align="right"><strong>{available}</strong></TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ОПЕРАЦІЇ */}
      {tabValue === 1 && (
        <>
          <TableContainer component={Paper}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel active={sortField === 'date'} direction={sortDir} onClick={() => handleSort('date')}>
                      Дата
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Тип</TableCell>
                  <TableCell>Замовлення / причина</TableCell>
                  <TableCell>Артикул</TableCell>
                  <TableCell>
                    <TableSortLabel active={sortField === 'name'} direction={sortDir} onClick={() => handleSort('name')}>
                      Виріб
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right">
                    <TableSortLabel active={sortField === 'quantity'} direction={sortDir} onClick={() => handleSort('quantity')}>
                      Кількість
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Хто</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {operations.length === 0 ? (
                  <TableRow><TableCell colSpan={7} align="center"><Typography color="textSecondary">Немає операцій</Typography></TableCell></TableRow>
                ) : operations.map((op) => (
                  <TableRow key={`${op.op_type}-${op.id}`}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDt(op.date)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={op.op_type === 'in' ? 'Прихід' : 'Відвантаження'}
                        color={op.op_type === 'in' ? 'success' : 'warning'} />
                    </TableCell>
                    <TableCell>
                      {op.order_display || (op.product_name ? `${op.product_name} x${op.quantity}` : '—')}
                      {op.note ? (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {op.note}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{op.product_article || '—'}</TableCell>
                    <TableCell>{op.product_name}</TableCell>
                    <TableCell align="right"><strong>{op.quantity}</strong></TableCell>
                    <TableCell>{op.created_by || '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* НАДХОДЖЕННЯ */}
      {tabValue === 2 && (
        <TableContainer component={Paper}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>Дата</TableCell>
                <TableCell>Замовлення</TableCell>
                <TableCell>Артикул</TableCell>
                <TableCell>Виріб</TableCell>
                <TableCell>Склад</TableCell>
                <TableCell align="right">Кількість</TableCell>
                <TableCell>Підтверджено</TableCell>
                <TableCell align="right">Дії</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPendingReceipts.length === 0 ? (
                <TableRow><TableCell colSpan={8} align="center"><Typography color="textSecondary">Немає непідтверджених надходжень</Typography></TableCell></TableRow>
              ) : filteredPendingReceipts.map((r) => (
                <TableRow key={r.id}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDt(r.created_at)}</TableCell>
                  <TableCell>{r.order_display || (r.product_name ? `${r.product_name} x${r.quantity}` : '—')}</TableCell>
                  <TableCell>{r.product_article || '—'}</TableCell>
                  <TableCell>{r.product_name}</TableCell>
                  <TableCell>{r.warehouse || 'Склад готової продукції'}</TableCell>
                  <TableCell align="right">{r.quantity}</TableCell>
                  <TableCell>{r.warehouse_confirmed ? 'Так' : 'Ні'}</TableCell>
                  <TableCell align="right">
                    <Button size="small" variant="contained" onClick={() => handleOpenArrivalConfirm(r)}>
                      Підтвердити
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ---- Arrival dialog (вільний залишок) ---- */}
      <Dialog open={arrivalOpen} onClose={() => !arrivalSaving && setArrivalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Прихід на склад</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            {arrivalError && <Typography color="error">{arrivalError}</Typography>}
            <Typography variant="body2" color="text.secondary">
              Ручний прихід у вільний залишок (без привʼязки до замовлення). Прихід з виробництва — через «Надходження».
            </Typography>
            <Autocomplete
              options={arrivalProducts}
              getOptionLabel={(p) => {
                const art = (p.article || '').trim();
                const name = (p.name || '').trim();
                if (art && name) return `${art} — ${name}`;
                return art || name || `#${p.id}`;
              }}
              value={arrivalProducts.find((p) => String(p.id) === String(arrivalForm.product_id)) || null}
              onChange={(_, val) => setArrivalForm((f) => ({ ...f, product_id: val ? val.id : '' }))}
              renderInput={(params) => (
                <TextField {...params} label="Виріб" required fullWidth />
              )}
              ListboxProps={{ style: { maxHeight: 'min(40vh, 320px)' } }}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter((p) => {
                  const art = (p.article || '').toLowerCase();
                  const name = (p.name || '').toLowerCase();
                  return art.includes(q) || name.includes(q);
                });
              }}
              noOptionsText="Нічого не знайдено"
            />
            <TextField
              label="Кількість, шт"
              type="number"
              value={arrivalForm.quantity}
              onChange={(e) => setArrivalForm((f) => ({ ...f, quantity: e.target.value }))}
              fullWidth
              required
              inputProps={{ min: 1, step: 1 }}
            />
            <TextField
              select
              label="Причина"
              value={arrivalForm.reason}
              onChange={(e) => setArrivalForm((f) => ({ ...f, reason: e.target.value }))}
              fullWidth
              required
            >
              <MenuItem value="">— оберіть —</MenuItem>
              {FG_MANUAL_REASONS.map((r) => (
                <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Коментар"
              value={arrivalForm.note}
              onChange={(e) => setArrivalForm((f) => ({ ...f, note: e.target.value }))}
              fullWidth
              multiline
              minRows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArrivalOpen(false)} disabled={arrivalSaving}>Скасувати</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleArrivalSubmit}
            disabled={arrivalSaving}
          >
            {arrivalSaving ? <CircularProgress size={20} /> : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Arrival confirm dialog ---- */}
      <Dialog open={arrivalConfirmOpen} onClose={() => setArrivalConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Підтвердити надходження</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {selectedReceipt?.order_display || (selectedReceipt?.product_name ? `${selectedReceipt.product_name} x${selectedReceipt.quantity}` : '—')}
          </Typography>
          <TextField label="Кількість (факт)" type="number" fullWidth margin="normal"
            value={confirmQuantity} onChange={(e) => setConfirmQuantity(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setArrivalConfirmOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleArrivalConfirmSubmit}>Підтвердити</Button>
        </DialogActions>
      </Dialog>

      {/* ---- Shipment dialog (multi-line) ---- */}
      <Dialog open={shipmentOpen} onClose={() => !shipmentSaving && setShipmentOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Відвантаження готової продукції</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {shipmentLines.map((line, idx) => {
              const stock = findStockOption(line.order_id, line.product_id);
              const byOrder = stock ? Number(stock.quantity) || 0 : 0;
              const reserved = reservedQtyFor(line.order_id, line.product_id, line.key);
              const maxQty = Math.max(0, byOrder - reserved);
              const selectedKeys = new Set(
                shipmentLines
                  .filter((l) => l.key !== line.key && l.order_id && l.product_id)
                  .map((l) => lineStockKey(l.order_id, l.product_id)),
              );
              const qty = parseInt(line.quantity, 10);
              return (
                <Box
                  key={line.key}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      Позиція {idx + 1}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveShipmentLine(line.key)}
                      disabled={shipmentSaving || shipmentLines.length <= 1}
                      aria-label="Видалити позицію"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <FormControl fullWidth required size="small">
                    <InputLabel>Позиція на складі</InputLabel>
                    <Select
                      value={
                        line.order_id && line.product_id
                          ? lineStockKey(line.order_id, line.product_id)
                          : ''
                      }
                      label="Позиція на складі"
                      disabled={shipmentSaving}
                      MenuProps={SELECT_MENU_PROPS}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (!raw) {
                          handleShipmentLineSelect(line.key, '', '');
                          return;
                        }
                        const [oid, pid] = String(raw).split(':');
                        handleShipmentLineSelect(line.key, oid, pid);
                      }}
                    >
                      <MenuItem value="">— оберіть —</MenuItem>
                      {shipmentStockOptions.length === 0 ? (
                        <MenuItem value="" disabled>
                          Немає залишків для відвантаження
                        </MenuItem>
                      ) : shipmentStockOptions.map((r) => {
                        const k = lineStockKey(r.order_id, r.product_id);
                        const taken = selectedKeys.has(k);
                        const art = (r.product_article || '').trim();
                        const orderPart = (r.is_free || r.order_id == null)
                          ? 'Вільний залишок — '
                          : `${(r.order_label && String(r.order_label).trim()) || orderRefFromRow(r)} — `;
                        return (
                          <MenuItem key={k} value={k} disabled={taken}>
                            {orderPart}
                            {art ? `${art} · ` : ''}
                            {r.product_name}
                            {` (на складі: ${r.quantity} шт)`}
                            {taken ? ' — уже додано' : ''}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>

                  {stock && (
                    <>
                      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Виріб</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {stock.product_article
                              ? `${stock.product_article} — ${stock.product_name}`
                              : stock.product_name}
                          </Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {(stock.is_free || stock.order_id == null) ? 'Вільний залишок' : 'По цьому замовленню'}
                          </Typography>
                          <Typography variant="body2" fontWeight={600}>{byOrder} шт</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Всього на складі</Typography>
                          <Typography variant="body2" fontWeight={600}>
                            {stockTotalByProduct(Number(stock.product_id))} шт
                          </Typography>
                        </Box>
                      </Box>
                      <TextField
                        label="Кількість до відвантаження *"
                        type="number"
                        size="small"
                        value={line.quantity}
                        onChange={(e) => updateShipmentLine(line.key, { quantity: e.target.value })}
                        fullWidth
                        required
                        disabled={shipmentSaving}
                        inputProps={{ min: 1, max: maxQty }}
                        helperText={
                          reserved > 0
                            ? `Максимум: ${maxQty} шт (залишок ${byOrder}, уже в формі ${reserved})`
                            : `Максимум: ${maxQty} шт`
                        }
                      />

                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                          Пакування (списується автоматично)
                        </Typography>
                        {(line.packaging || []).length === 0 ? (
                          <Typography variant="body2" color="text.secondary">
                            У виробі не вказано пакування.
                          </Typography>
                        ) : (
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Пакувальний матеріал</TableCell>
                                <TableCell align="right">Шт. в упаковці</TableCell>
                                <TableCell align="right">Кількість упаковок</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {line.packaging.map((comp) => {
                                const perPkg = comp.quantity || 1;
                                const pkgs = Number.isFinite(qty) && qty > 0
                                  ? Math.ceil(qty / perPkg)
                                  : '—';
                                return (
                                  <TableRow key={comp.id}>
                                    <TableCell>{comp.packaging_material_name || comp.packaging_material}</TableCell>
                                    <TableCell align="right">{perPkg}</TableCell>
                                    <TableCell align="right"><strong>{pkgs}</strong></TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              );
            })}

            <Button
              startIcon={<AddIcon />}
              onClick={handleAddShipmentLine}
              disabled={shipmentSaving || shipmentStockOptions.length === 0}
              sx={{ alignSelf: 'flex-start' }}
            >
              Додати позицію
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShipmentOpen(false)} disabled={shipmentSaving}>Скасувати</Button>
          <Button variant="contained" color="warning" onClick={handleShipmentSubmit} disabled={shipmentSaving}>
            {shipmentSaving ? 'Збереження...' : 'Зберегти'}
          </Button>
        </DialogActions>
      </Dialog>

      <ManualWarehouseTransferDialog
        hubOpen={transferHubOpen}
        onHubClose={() => setTransferHubOpen(false)}
        sourceWarehouseId="finished"
        formTitle="Ручне переміщення ГП"
        hubHint="Перемістіть готову продукцію на будь-який склад системи. Рух зʼявиться в журналі; залишки оновляться одразу."
        options={shipmentStockOptions}
        getOptionKey={(r) => lineStockKey(r.order_id, r.product_id)}
        getOptionQty={(r) => Number(r.quantity) || 0}
        getOptionLabel={(r) => {
          const orderLabel = (r.is_free || r.order_id == null)
            ? 'Вільний залишок'
            : (r.order_label || orderRefFromRow(r));
          const art = (r.product_article || '').trim();
          const name = (r.product_name || '').trim();
          const prod = art && name ? `${art} — ${name}` : (art || name || '—');
          return `${orderLabel}: ${prod} — ${r.quantity} шт`;
        }}
        qtyLabel="Кількість, шт"
        onSubmit={async ({ option, quantity, destination, note }) => {
          await finishedGoodsBalancesService.transferOut({
            order_id: option.order_id == null ? null : option.order_id,
            product_id: productIdOf(option),
            quantity: Math.round(quantity),
            destination,
            note,
          });
          await loadData();
        }}
      />
    </Box>
  );
}

function productIdOf(option) {
  return option.product_id || option.product;
}

export default FinishedGoodsStockPage;
